import 'font-awesome/css/font-awesome.css';
import './tailwind.generated.css';

import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';

import 'bz-core-controls/utils/reflect-metadata';

import 'bz-utilities/global';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { View } from '@zinc/ui';
import LoadingPanel from 'bz-core-controls/controls/layout/loading-panel';
import Fallback from 'bz-core-controls/controls/lazy/controls/fallback';
import BzErrorBoundary from 'bz-core-controls/errors/controls/error-boundary';
import { setLegacyColourVariables } from 'bz-core-controls/styles/colours';
import { BzThemeStoreProvider } from 'bz-core-controls/styles/theme/store/theme';
import { Register } from 'bz-icons';
import AuthStore, { AuthStoreContext } from 'bz-stores/lib/auth-store';
import history from 'bz-utilities/modules/networking/history/rr4';
import VersionCheck from 'bz-utilities/modules/version-check';
import { LoginRoutePaths } from 'core/auth';
import { observer, Provider } from 'mobx-react';
import React from 'react';
import { Toaster } from 'react-hot-toast';
import { Route, Router, Switch } from 'react-router-dom';
import ModuleFeatureProvider from 'tm3-core/controls/module/provider';
import z from 'z';

const RpPrint = React.lazy(() => import('./components/print'));
const Auth = React.lazy(() => import('./components/auth'));
const AuthenticatedApp = React.lazy(() => import('./shell/app'));

window.React = React;

setLegacyColourVariables();

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 30 /* 30 seconds */ } }
});

z.setApplication('Referral Portal');

const AuthLoader = observer(() => {
  try {
    if (AuthStore.authenticated) {
      return (
        <Switch>
          <Route path={'/print'} component={RpPrint} />
          <Route
            path={'/'}
            render={() => (
              <ModuleFeatureProvider authenticated={true}>
                <AuthenticatedApp />
              </ModuleFeatureProvider>
            )}
          />
        </Switch>
      );
    }
    AuthStore.checkReferralPortalLoginState();
    return (
      <div className="h-full w-full">
        <LoadingPanel />
      </div>
    );
  } catch {
    return (
      <div className="h-full w-full">
        <LoadingPanel />
      </div>
    );
  }
});

const ReferralPortal: React.FC = observer(() => {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <VersionCheck />
        <BzThemeStoreProvider>
          <View backgroundColor="page" className="referral-portal-app">
            <Toaster
              position="top-right"
              toastOptions={{
                success: { duration: 60000 },
                error: { duration: 8000 }
              }}
            />
            <BzErrorBoundary>
              <Provider authStore={AuthStore} historyStore={history} license={LicenceHack}>
                <AuthStoreContext.Provider value={AuthStore}>
                  <Register>
                    <React.Suspense fallback={<Fallback />}>
                      <Router history={history.browserHistory}>
                        <Switch>
                          <Route path={LoginRoutePaths} component={Auth} />
                          <Route component={AuthLoader} />
                        </Switch>
                      </Router>
                    </React.Suspense>
                  </Register>
                </AuthStoreContext.Provider>
              </Provider>
            </BzErrorBoundary>
          </View>
        </BzThemeStoreProvider>
      </QueryClientProvider>
    </>
  );
});

ReferralPortal.displayName = 'ReferralPortal';

class LicenseHackStore {
  size = 1;
  get(key: string) {
    if (AuthStore.user?.meta) return AuthStore.user.meta[key].toString().toUpperCase() === 'TRUE';
    return false;
  }
}
export const LicenceHack = new LicenseHackStore();

export default ReferralPortal;
